import { useRef, useState } from 'react';
import { uploadAttachment, type UploadFailure } from '../lib/api.ts';

interface Props {
  readonly messageId: string;
  readonly onUploaded: () => void;
}

export const REASON: Record<UploadFailure, string> = {
  unsupported_type: 'Only photos and PDFs can be attached.',
  too_large: 'Too big. Photos up to 10MB, PDFs up to 25MB.',
  corrupt_image: 'That image could not be read.',
  no_file: 'No file was selected.',
  unknown: 'Upload failed. Try again.',
};

/**
 * capture="environment" makes a phone open the rear camera directly
 * rather than the photo library. A maintenance report is nearly always
 * someone standing in front of the broken thing.
 */
export const AttachButton = ({ messageId, onUploaded }: Props) => {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  /**
   * One at a time, in order.
   *
   * The API attaches a single file per call, and uploading in
   * parallel from a counter PC on retail broadband is how you get
   * three timeouts instead of three attachments. Stopping at the
   * first failure means the person sees which file was rejected.
   */
  const pick = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError('');

    for (const file of Array.from(files)) {
      const result = await uploadAttachment(messageId, file);
      if (!result.ok) {
        setError(`${file.name}: ${REASON[result.reason]}`);
        break;
      }
      onUploaded();
    }

    setBusy(false);
    // Reset so re-selecting the same file fires change again.
    if (input.current) input.current.value = '';
  };

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        hidden
        multiple
        onChange={(e) => void pick(e.target.files)}
      />
      <button
        className="attachBtn iconBtn"
        disabled={busy}
        title="Attach a photo or PDF"
        aria-label="Attach a photo or PDF"
        onClick={() => input.current?.click()}
      >
        {busy ? (
          <span className="mono">...</span>
        ) : (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 11.5 L11 20.5 a4.2 4.2 0 0 1-6-6 L14 5.5
                     a2.8 2.8 0 0 1 4 4 L9.5 18" />
          </svg>
        )}
      </button>
      {error && <p className="err">{error}</p>}
    </>
  );
};
